import React, { Fragment, useState } from 'react';
import { VirtualBrand, VirtualPack } from '~/Api/Analytics/VirtualBrand';
import { AnalyticDataSources } from '~/Api/Analytics/VirtualBrand/mapAnalyticVirtualBrand';
import { Country } from '~/Api/Country';
import { Delayed } from '~/Delayed';
import groupArray from '~/utils/groupArray';
import { RegionAvailability } from '~/MedicinalBrandsAnalytics/Region';
import AvailabilityMap from '~/MedicinalBrandsAnalytics/VirtualBrand/AvailabilityMap';
import AvailabilityTable from '~/MedicinalBrandsAnalytics/VirtualBrand/AvailabilityTable';
import CountryAutocomplete from '~/MedicinalBrandsAnalytics/VirtualBrand/CountryAutocomplete';
import RegionSelect from '~/MedicinalBrandsAnalytics/VirtualBrand/RegionSelect';

import styles from './styles.less';

export interface TabState {
	regionAvailability: RegionAvailability | null;
	countryAutocompleteSelectedOptions: { country: Country }[];
}

interface Props {
	virtualBrand: VirtualBrand;
	virtualPack: VirtualPack | null;
	virtualBrandAvailability: { [countryCode: string]: AnalyticDataSources };
	virtualPackAvailability: { [virtualPackId: string]: { [countryCode: string]: AnalyticDataSources } };
	countries: Country[];
	allDatasourceCountries: Country[];
	defaultRegionAvailability: RegionAvailability | null;
	state: TabState;
	onStateChange(nextTabState: TabState): void;
}

const initialState = {
	regionAvailability: null,
	countryAutocompleteSelectedOptions: [],
};

export default function AvailabilityTab({
	virtualBrand,
	virtualPack,
	countries,
	allDatasourceCountries,
	defaultRegionAvailability,
	virtualBrandAvailability,
	virtualPackAvailability,
	state = initialState,
	onStateChange,
}: Props) {
	const [countryAutocompleteValue, setCountryAutocompleteValue] = useState('');

	function renderAvailabilityTable(regionCountries: Country[], filterCountries: Country[]) {
		return (
			<Fragment>
				<div className={styles.select}>
					<CountryAutocomplete
						hideOnSelect
						multiple
						countries={regionCountries}
						// @ts-expect-error strange usage of options
						selectedOptions={state.countryAutocompleteSelectedOptions}
						value={countryAutocompleteValue}
						onChange={(countryAutocompleteValue) => setCountryAutocompleteValue(countryAutocompleteValue)}
						// @ts-expect-error strange usage of options
						onSelect={(options: { country: Country }[]) => {
							setCountryAutocompleteValue('');
							onStateChange({
								...state,
								countryAutocompleteSelectedOptions: options,
							});
						}}
					/>
				</div>
				{virtualPack ? (
					<AvailabilityTable
						countries={filterCountries}
						virtualPack={virtualPack}
						virtualPackAvailability={virtualPackAvailability[virtualPack.id] || {}}
					/>
				) : null}
				{virtualPack ? <hr /> : null}

				{groupArray(virtualBrand.sortedVirtualPacks, 3).map((virtualPacks, i) => {
					const tables = virtualPacks.map((virtualPack) => (
						<AvailabilityTable
							countries={filterCountries}
							key={virtualPack.id}
							virtualPack={virtualPack}
							virtualPackAvailability={virtualPackAvailability[virtualPack.id] || {}}
						/>
					));
					return i > 0 ? (
						<Delayed key={virtualPacks.map((virtualPack) => virtualPack.id).join(',')} milliseconds={i * 5}>
							{tables}
						</Delayed>
					) : (
						tables
					);
				})}
			</Fragment>
		);
	}

	if (!defaultRegionAvailability) {
		return null;
	}

	const regionAvailability =
		state.regionAvailability === undefined ? defaultRegionAvailability : state.regionAvailability;
	const regionCountries = regionAvailability ? regionAvailability.countries : countries;
	const filterCountries =
		state.countryAutocompleteSelectedOptions.length > 0
			? state.countryAutocompleteSelectedOptions.map((option) => option.country)
			: regionCountries;
	const availability = virtualPack ? virtualPackAvailability[virtualPack.id] : virtualBrandAvailability;

	return (
		<div className={styles.content}>
			<div className={styles.leftColumn}>
				<RegionSelect
					availability={availability || {}}
					selectedRegionAvailability={regionAvailability}
					onSelect={(regionAvailability) =>
						onStateChange({
							...state,
							regionAvailability,
							countryAutocompleteSelectedOptions: [],
						})
					}
				/>
			</div>

			{regionAvailability && regionAvailability.region.isInEurope ? (
				<div className={styles.rightColumn}>
					<div className={styles.map}>
						<AvailabilityMap
							availablePacks={availability}
							datasourceCountries={allDatasourceCountries}
							regionCountries={regionAvailability.region.countries}
							hasSelectedPackage={!!virtualPack}
						/>
					</div>

					<div className={styles.table}>{renderAvailabilityTable(regionCountries, filterCountries)}</div>
				</div>
			) : (
				<div className={styles.rightColumn}>{renderAvailabilityTable(regionCountries, filterCountries)}</div>
			)}
		</div>
	);
}
